import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin, MessageCircle } from 'lucide-react';

const Footer = () => {
  const { t } = useLanguage();

  const quickLinks = [
    { label: { en: 'About Us', ar: 'من نحن' }, path: '/about' },
    { label: { en: 'Properties', ar: 'العقارات' }, path: '/properties' },
    { label: { en: 'Services', ar: 'الخدمات' }, path: '/services' },
    { label: { en: 'Blog', ar: 'المدونة' }, path: '/blog' },
    { label: { en: 'Contact', ar: 'اتصل بنا' }, path: '/contact' }
  ];

  const services = [
    { label: { en: 'Commercial', ar: 'تجاري' }, path: '/services#commercial' },
    { label: { en: 'Residential', ar: 'سكني' }, path: '/services#residential' },
    { label: { en: 'Off-Plan', ar: 'على الخارطة' }, path: '/services#offplan' },
    { label: { en: 'Investment Advisory', ar: 'استشارات استثمارية' }, path: '/services#advisory' }
  ];

  return (
    <>
    <footer className="bg-[#12532c] text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_netgains-redesign/artifacts/97d5giqr_logo-vectorfile%20%283%29.png" 
              alt="Net Gains Real Estate" 
              className="h-16 w-auto mb-3"
              data-testid="footer-logo"
            />
            {/* Tagline - aligned with logo edge */}
            <div className="mb-4 pb-4 border-b border-white/10" data-testid="footer-tagline">
              <p className="text-[#C5A038] font-semibold text-sm tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t({ en: 'Trusted for Value, Remembered for Integrity', ar: 'موثوقون في القيمة، نُذكر بالنزاهة' })}
              </p>
            </div>
            <p className="text-sm mb-4">
              {t({
                en: 'Your strategic partner in Dubai commercial & residential real estate. Building wealth through property.',
                ar: 'شريكك الاستراتيجي في العقارات التجارية والسكنية بدبي. بناء الثروة من خلال العقارات.'
              })}
            </p>
            <div className="flex space-x-4">
              <a href="https://facebook.com/netgainsrealestate" target="_blank" rel="noopener noreferrer" className="hover:text-[#C5A038] transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://instagram.com/netgainsrealestate" target="_blank" rel="noopener noreferrer" className="hover:text-[#C5A038] transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://linkedin.com/company/netgainsrealestate" target="_blank" rel="noopener noreferrer" className="hover:text-[#C5A038] transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="https://twitter.com/netgainsre" target="_blank" rel="noopener noreferrer" className="hover:text-[#C5A038] transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">
              {t({ en: 'Quick Links', ar: 'روابط سريعة' })}
            </h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="hover:text-[#C5A038] transition-colors text-sm"
                  >
                    {t(link.label)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-4">
              {t({ en: 'Our Services', ar: 'خدماتنا' })}
            </h4>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.path}>
                  <Link
                    to={service.path}
                    className="hover:text-[#C5A038] transition-colors text-sm"
                  >
                    {t(service.label)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">
              {t({ en: 'Contact Us', ar: 'اتصل بنا' })}
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span className="text-sm">
                  {t({
                    en: 'European Business Centre, Office BC-50, 3rd Floor, Dubai Investment Park First, Green Community Village, Dubai, UAE',
                    ar: 'المركز التجاري الأوروبي، مكتب BC-50، الطابق الثالث، مجمع دبي للاستثمار الأول، دبي، الإمارات'
                  })}
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm" data-testid="footer-phone">+971 52 106 1252</span>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm" data-testid="footer-email">info@netgainsrealestate.ae</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#1a6b52] pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Net Gains Real Estate LLC.{' '}
              {t({ en: 'All rights reserved.', ar: 'جميع الحقوق محفوظة.' })}
            </p>
            <div className="flex items-center flex-wrap justify-center gap-6">
              <Link to="/privacy" className="text-sm text-gray-400 hover:text-[#C5A038] transition-colors">
                {t({ en: 'Privacy Policy', ar: 'سياسة الخصوصية' })}
              </Link>
              <Link to="/terms" className="text-sm text-gray-400 hover:text-[#C5A038] transition-colors">
                {t({ en: 'Terms of Service', ar: 'شروط الخدمة' })}
              </Link>
              {/* Admin link removed from public footer for security. Access via /admin/login directly. */}
            </div>
          </div>
        </div>
      </div>
    </footer>
    
    {/* Floating WhatsApp Button */}
    <a
      href="https://wa.me/971521061252?text=Hello,%20I%20would%20like%20to%20inquire%20about%20your%20properties"
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#20ba5a] text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 group"
      aria-label="Contact us on WhatsApp"
    >
      <MessageCircle className="w-6 h-6" />
      <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        {t({ en: 'Chat with us on WhatsApp', ar: 'تحدث معنا على واتساب' })}
      </span>
    </a>
  </>
  );
};

export default Footer;
